import { useState, useEffect, useRef } from "react";
import { X, Share, PlusSquare, Download, MoreVertical, MoreHorizontal, Monitor, Smartphone, ExternalLink, Star, ChevronLeft, Shield, Zap, Bell, Lock } from "lucide-react";
import { useI18n } from "@/lib/i18n";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true
    || localStorage.getItem("pwa_installed") === "true";
}

function getDeviceInfo() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  const isChrome = /Chrome|CriOS/.test(ua) && !/Edge|Edg|OPR/.test(ua);
  const isEdge = /Edg/.test(ua);
  const isFirefox = /Firefox|FxiOS/.test(ua);
  const isMac = /Macintosh/.test(ua);

  if (isIOS && isSafari) return "ios-safari";
  if (isIOS) return "ios-chrome";
  if (isAndroid && isChrome) return "android-chrome";
  if (isAndroid) return "android-other";
  if (isChrome || isEdge) return "desktop-chrome";
  if (isFirefox) return "desktop-firefox";
  if (isSafari && isMac) return "desktop-safari";
  return "desktop-other";
}

function StepBadge({ n }) {
  return (
    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 text-white text-xs font-bold flex items-center justify-center shadow-lg shadow-cyan-500/25">
      {n}
    </span>
  );
}

function StarsDisplay({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.floor(rating) ? "text-amber-400 fill-amber-400" : i <= rating ? "text-amber-400 fill-amber-400/50" : "text-zinc-700"}`} />
      ))}
    </div>
  );
}

function InstructionStep({ n, icon, text, highlight }) {
  return (
    <div className={`flex items-center gap-3 p-3.5 rounded-xl transition-colors duration-200 ${
      highlight
        ? "bg-cyan-500/10 border border-cyan-500/20 shadow-[0_0_12px_rgba(34,211,238,0.04)]"
        : "bg-zinc-800/30 border border-zinc-800/50 hover:border-zinc-700/50"
    }`}>
      <StepBadge n={n} />
      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        {icon}
        <span className={`text-sm leading-snug ${highlight ? "text-cyan-300 font-medium" : "text-zinc-300"}`}>{text}</span>
      </div>
    </div>
  );
}


function AppStorePage({ device, t, onClose, onInstall, deferredPrompt }) {
  const [showInstructions, setShowInstructions] = useState(false);
  const canNativeInstall = deferredPrompt != null;
  const scrollRef = useRef(null);

  const handleInstallClick = () => {
    if (canNativeInstall) {
      onInstall();
    } else {
      setShowInstructions(true);
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      }, 100);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-zinc-950">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800/30 safe-area-top">
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={onClose} className="flex items-center gap-1 text-cyan-400 text-sm font-medium hover:text-cyan-300 transition-colors">
            <ChevronLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
          {showInstructions && canNativeInstall && (
            <button
              onClick={onInstall}
              className="px-5 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-bold rounded-full shadow-lg shadow-cyan-500/20"
            >
              Installer
            </button>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="overflow-y-auto h-[calc(100vh-52px)] overscroll-y-contain">
        {/* App header */}
        <div className="px-5 pt-6 pb-5">
          <div className="flex items-start gap-4">
            <div className="w-[100px] h-[100px] rounded-[22px] bg-gradient-to-br from-cyan-500 via-blue-600 to-purple-600 flex items-center justify-center shadow-xl shadow-cyan-500/20 flex-shrink-0 overflow-hidden ring-1 ring-white/10">
              <img src="/icons/icon-192.png" alt="ShadowMail" className="w-full h-full object-cover" onError={(e) => {
                e.target.style.display = "none";
                e.target.parentElement.innerHTML = '<div class="flex flex-col items-center"><svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"/><path d="m3 7 9 6 9-6"/></svg><span class="text-white text-[10px] font-bold mt-1">SM</span></div>';
              }} />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h1 className="text-white text-xl font-bold tracking-tight">ShadowMail</h1>
              <p className="text-zinc-500 text-sm mt-0.5">Emails temporaires sécurisés</p>
              <button
                onClick={handleInstallClick}
                className="mt-3 px-7 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-bold rounded-full hover:from-cyan-400 hover:to-blue-400 transition-colors shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-95"
              >
                Installer
              </button>
            </div>
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: "ShadowMail",
                    text: "Service de mail temporaire anonyme et gratuit",
                    url: window.location.origin,
                  }).catch(() => {});
                } else {
                  navigator.clipboard.writeText(window.location.origin).then(() => {
                    const btn = document.getElementById("share-btn-tooltip");
                    if (btn) { btn.textContent = "Copié !"; setTimeout(() => { btn.textContent = ""; }, 2000); }
                  }).catch(() => {});
                }
              }}
              className="p-2.5 rounded-xl bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:bg-cyan-500/10 hover:border-cyan-500/30 hover:text-cyan-400 transition-colors mt-1 flex-shrink-0 relative"
            >
              <Share className="w-5 h-5" />
              <span id="share-btn-tooltip" className="absolute -bottom-6 right-0 text-[10px] text-cyan-400 whitespace-nowrap" />
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="px-5 py-5 border-y border-zinc-800/30">
          <div className="flex items-center justify-around">
            <div className="text-center group/stat">
              <div className="text-white text-lg font-bold tracking-tight">4.9</div>
              <StarsDisplay rating={4.9} />
              <div className="text-zinc-600 text-[10px] mt-1 uppercase tracking-widest font-medium">Notes</div>
            </div>
            <div className="w-px h-12 bg-gradient-to-b from-transparent via-zinc-700/50 to-transparent" />
            <div className="text-center group/stat">
              <div className="text-white text-lg font-bold tracking-tight">&lt;1</div>
              <div className="text-zinc-500 text-xs font-medium">MB</div>
              <div className="text-zinc-600 text-[10px] mt-1 uppercase tracking-widest font-medium">Taille</div>
            </div>
            <div className="w-px h-12 bg-gradient-to-b from-transparent via-zinc-700/50 to-transparent" />
            <div className="text-center group/stat">
              <div className="text-white text-lg font-bold flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-cyan-400" />
                </div>
              </div>
              <div className="text-zinc-500 text-xs font-medium mt-0.5">Chiffré</div>
              <div className="text-zinc-600 text-[10px] mt-0.5 uppercase tracking-widest font-medium">Sécurité</div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="px-5 py-5">
          <p className="text-zinc-300 text-sm leading-relaxed">
            Gérez vos emails temporaires en toute confidentialité. Créez des adresses jetables instantanément, recevez et envoyez des emails, le tout chiffré et sécurisé.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              { icon: <Zap className="w-3 h-3" />, label: "Rapide", color: "text-amber-400 border-amber-500/20 bg-amber-500/5" },
              { icon: <Lock className="w-3 h-3" />, label: "Sécurisé", color: "text-emerald-400 border-emerald-500/20 bg-emerald-500/5" },
              { icon: <Bell className="w-3 h-3" />, label: "Notifications", color: "text-cyan-400 border-cyan-500/20 bg-cyan-500/5" },
            ].map(f => (
              <span key={f.label} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium ${f.color}`}>
                {f.icon} {f.label}
              </span>
            ))}
          </div>
        </div>

        {/* Developer info */}
        <div className="px-5 py-4 border-t border-zinc-800/30">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/10 border border-cyan-500/20 flex items-center justify-center">
              <img src="/icons/icon-96.png" alt="" className="w-6 h-6 rounded" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <div>
              <div className="text-sm font-semibold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">ShadowMail</div>
              <div className="text-zinc-600 text-[11px]">Développeur</div>
            </div>
          </div>
        </div>

        {/* Information table */}
        <div className="px-5 py-5 border-t border-zinc-800/30">
          <h3 className="text-white font-semibold mb-4 tracking-tight">Information</h3>
          <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/40 rounded-xl overflow-hidden">
            {[
              ["Fournisseur", "ShadowMail"],
              ["Taille", "< 1 MB"],
              ["Catégorie", "Utilitaires"],
              ["Compatibilité", device.startsWith("ios") ? "Fonctionne sur cet iPhone" : device.startsWith("android") ? "Fonctionne sur cet appareil" : "Fonctionne sur cet ordinateur"],
              ["Langues", "Français, English"],
              ["Prix", "Gratuit"],
            ].map(([k, v], i) => (
              <div key={k} className={`flex items-center justify-between px-4 py-3.5 ${i > 0 ? "border-t border-zinc-800/30" : ""}`}>
                <span className="text-zinc-500 text-sm">{k}</span>
                <span className="text-zinc-300 text-sm font-medium">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Install instructions */}
        {showInstructions && (
          <div className="px-5 py-5 border-t border-cyan-500/15">
            <div className="bg-gradient-to-br from-cyan-500/5 to-blue-500/5 border border-cyan-500/15 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-cyan-500/15 border border-cyan-500/20 flex items-center justify-center">
                  <Download className="w-3.5 h-3.5 text-cyan-400" />
                </div>
                Comment installer
              </h3>
              <div className="space-y-2.5">
                {device === "ios-safari" && (
                  <>
                    <InstructionStep n={1} icon={<MoreHorizontal className="w-4 h-4 text-cyan-400" />} text={t.iosSafariStep1} />
                    <InstructionStep n={2} icon={<Share className="w-4 h-4 text-cyan-400" />} text={t.iosSafariStep2} />
                    <InstructionStep n={3} icon={<span className="text-cyan-400 text-sm">↓</span>} text={t.iosSafariStep3} />
                    <InstructionStep n={4} icon={<PlusSquare className="w-4 h-4 text-cyan-400" />} text={t.iosSafariStep4} highlight />
                  </>
                )}
                {(device === "ios-chrome" || device === "ios-other") && (
                  <>
                    <InstructionStep n={1} icon={<MoreHorizontal className="w-4 h-4 text-cyan-400" />} text={t.iosChromeStep1} />
                    <InstructionStep n={2} icon={<ExternalLink className="w-4 h-4 text-cyan-400" />} text={t.iosChromeStep2} />
                    <InstructionStep n={3} icon={<PlusSquare className="w-4 h-4 text-cyan-400" />} text={t.iosChromeStep3} highlight />
                  </>
                )}
                {(device === "android-chrome" || device === "android-other") && (
                  <>
                    <InstructionStep n={1} icon={<MoreVertical className="w-4 h-4 text-cyan-400" />} text={t.androidStep1} />
                    <InstructionStep n={2} icon={<PlusSquare className="w-4 h-4 text-cyan-400" />} text={t.androidStep2} />
                    <InstructionStep n={3} icon={<Download className="w-4 h-4 text-cyan-400" />} text={t.androidStep3} highlight />
                  </>
                )}
                {device === "desktop-chrome" && (
                  <>
                    <InstructionStep n={1} icon={<Monitor className="w-4 h-4 text-cyan-400" />} text={t.desktopChromeStep1} />
                    <InstructionStep n={2} icon={<MoreVertical className="w-4 h-4 text-cyan-400" />} text={t.desktopChromeStep2} />
                    <InstructionStep n={3} icon={<Download className="w-4 h-4 text-cyan-400" />} text={t.desktopChromeStep3} highlight />
                  </>
                )}
                {device === "desktop-firefox" && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                    <p className="text-amber-300 text-sm leading-relaxed">{t.desktopFirefoxInfo}</p>
                  </div>
                )}
                {device === "desktop-safari" && (
                  <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-4">
                    <p className="text-zinc-300 text-sm leading-relaxed">{t.desktopSafariInfo}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bottom padding */}
        <div className="h-8" />
      </div>
    </div>
  );
}

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [showStore, setShowStore] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [device] = useState(getDeviceInfo);
  const { t } = useI18n();

  useEffect(() => {
    if (isStandalone()) return;

    const dismissed = localStorage.getItem("install_prompt_dismissed");
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    const timer = setTimeout(() => setShow(true), 30000);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    setShowStore(false);
    localStorage.setItem("install_prompt_dismissed", Date.now().toString());
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (result.outcome === "accepted") {
        localStorage.setItem("pwa_installed", "true");
        dismiss();
      }
    }
  };

  // Auto-close when app is installed
  useEffect(() => {
    const onInstalled = () => {
      setShow(false);
      setShowStore(false);
      localStorage.setItem("pwa_installed", "true");
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  // Swipe-down to dismiss
  const bannerRef = useRef(null);
  const touchStart = useRef(null);
  const dragY = useRef(0);

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientY;
    dragY.current = 0;
    if (bannerRef.current) bannerRef.current.style.transition = "none";
  };

  const onTouchMove = (e) => {
    if (touchStart.current === null) return;
    const diff = e.touches[0].clientY - touchStart.current;
    if (diff > 0) {
      e.preventDefault();
      dragY.current = diff;
      if (bannerRef.current) {
        bannerRef.current.style.transform = `translateY(${diff}px)`;
        bannerRef.current.style.opacity = Math.max(0, 1 - diff / 150);
      }
    }
  };

  const swiped = useRef(false);

  const onTouchEnd = () => {
    if (bannerRef.current) bannerRef.current.style.transition = "transform 0.3s, opacity 0.3s";
    if (dragY.current > 80) {
      swiped.current = true;
      if (bannerRef.current) {
        bannerRef.current.style.transform = "translateY(200px)";
        bannerRef.current.style.opacity = "0";
        bannerRef.current.style.pointerEvents = "none";
      }
      setTimeout(dismiss, 300);
    } else {
      if (bannerRef.current) {
        bannerRef.current.style.transform = "translateY(0)";
        bannerRef.current.style.opacity = "1";
      }
    }
    touchStart.current = null;
    dragY.current = 0;
  };

  if (!show) return null;

  return (
    <>
      {/* Banner */}
      <div
        ref={bannerRef}
        className="fixed bottom-4 left-4 right-4 z-[100] animate-slide-up sm:left-auto sm:right-4 sm:max-w-sm"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Swipe indicator */}
        <div className="flex justify-center mb-1.5 sm:hidden">
          <div className="w-8 h-1 rounded-full bg-zinc-600/50" />
        </div>
        <div
          className="bg-zinc-900/95 border border-cyan-500/30 rounded-xl p-3.5 shadow-lg shadow-cyan-500/10 backdrop-blur-md cursor-pointer hover:border-cyan-500/50 transition-colors group"
          onClick={() => setShowStore(true)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); dismiss(); }}
            className="absolute top-2 right-2 text-zinc-600 hover:text-white transition-colors p-1 rounded-lg hover:bg-zinc-800"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 via-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-500/20 group-hover:scale-105 transition-transform overflow-hidden">
              <img src="/icons/icon-96.png" alt="" className="w-full h-full object-cover" onError={(e) => {
                e.target.style.display = "none";
              }} />
            </div>
            <div className="flex-1 min-w-0 pr-5">
              <p className="text-white text-sm font-semibold">ShadowMail</p>
              <p className="text-zinc-500 text-xs">Emails temporaires sécurisés</p>
              <div className="flex items-center gap-2 mt-1">
                <StarsDisplay rating={4.9} />
                <span className="text-zinc-600 text-[10px]">4.9</span>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setShowStore(true); }}
              className="px-4 py-1.5 bg-cyan-500/20 text-cyan-400 text-xs font-bold rounded-full border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors flex-shrink-0"
            >
              Installer
            </button>
          </div>
        </div>
      </div>

      {/* Full App Store page */}
      {showStore && (
        <AppStorePage
          device={device}
          t={t}
          onClose={() => setShowStore(false)}
          onInstall={handleInstall}
          deferredPrompt={deferredPrompt}
        />
      )}
    </>
  );
}
